export enum InventoryAdjustmentReasons {
  /**
   * The business used the stock itself: a kitchen cooking with it, staff drinking it, a tasting.
   * It only removes stock, so a person picks it under Remove and never under Add or Set.
   * It is not a loss, so it has its own type.
   */
  CONSUMPTION = 'consumption',
  RETURN = 'return',
  RECEIVED = 'received',
  THEFT_LOSS = 'theft_loss',
  DAMAGED = 'damaged',
  EXPIRED = 'expired',
  CORRECTION = 'correction',
  COUNT = 'count',
  DONATION = 'donation',
  CONSIGNMENT_RECEIVED = 'consignment.received',
  CONSIGNMENT_RETURNED = 'consignment.returned',
  CONSIGNMENT_CORRECTION = 'consignment.correction',
  /**
   * A person moved stock between two of the tenant's own locations.
   *
   * ⚠ **In both direction sets below, because a move is two adjustments.** The outward half is a
   * reduce and the inward half is an add, so `isReasonValid` has to accept it either way.
   *
   * ⚠ **Not on any dropdown.** Those live in `useQuickInventoryUpdate` and this is absent from
   * both, and `systemReserved()` keeps it out of the generic reasons API. A person picking it for
   * a one-sided adjustment would leave half a move behind — the ledger claiming stock went
   * somewhere it never arrived. Only the move flow writes it, and it writes both halves.
   */
  MOVED = 'moved'
}

/**
 * Which direction each reason may move stock. A reason in both sets may adjust either way.
 * `CONSIGNMENT_CORRECTION` is in neither: it targets the consigned quantity and is
 * bounds-checked against available stock instead of sign-checked.
 *
 * `isReasonValid` enforces these sets and the reason dropdown displays them, so the rule a
 * merchant sees while picking is the same one validation applies on save.
 */
const INVENTORY_ADJUSTMENT_ADD_REASONS: readonly InventoryAdjustmentReasons[] = [
  InventoryAdjustmentReasons.CORRECTION,
  InventoryAdjustmentReasons.RETURN,
  InventoryAdjustmentReasons.COUNT,
  InventoryAdjustmentReasons.DONATION,
  InventoryAdjustmentReasons.RECEIVED,
  InventoryAdjustmentReasons.CONSIGNMENT_RECEIVED,
  InventoryAdjustmentReasons.MOVED
]

const INVENTORY_ADJUSTMENT_REDUCE_REASONS: readonly InventoryAdjustmentReasons[] = [
  InventoryAdjustmentReasons.CONSUMPTION,
  InventoryAdjustmentReasons.THEFT_LOSS,
  InventoryAdjustmentReasons.DAMAGED,
  InventoryAdjustmentReasons.EXPIRED,
  InventoryAdjustmentReasons.CORRECTION,
  InventoryAdjustmentReasons.COUNT,
  InventoryAdjustmentReasons.DONATION,
  InventoryAdjustmentReasons.CONSIGNMENT_RETURNED,
  InventoryAdjustmentReasons.MOVED
]

/** Every reason a person may pick in a drawer. The move flow alone writes `moved`, so it is left out. */
const PICKABLE_REASONS: readonly InventoryAdjustmentReasons[] = Object.values(InventoryAdjustmentReasons).filter(
  reason => reason !== InventoryAdjustmentReasons.MOVED
)

export { INVENTORY_ADJUSTMENT_ADD_REASONS, INVENTORY_ADJUSTMENT_REDUCE_REASONS, PICKABLE_REASONS }
